import { useState, useEffect } from "react";

import BusinessInformation from "./Steps/RegisteredBusinessInformation";
import BusinessOwnersInformation from "./Steps/RegisteredBusinessOwnersInformation";
import RegisteredBusinessDocuments from "./Steps/RegisteredBusinessDocuments";
import CompleteOnboarding from "./Steps/RegisteredBusinessCompleteOnboarding";
import HeaderTitle from "../../Components/HeaderTitle";
import styles from "./RegisteredBusiness.module.scss";
import useDocumentsByCustomerSubType from "../../hooks/useDocumentsByCustomerSubType";
import { BusinessTypes } from "../../../Interfaces";

import { ICorporateCustomer } from "../../hooks/useCorporateCustomers";

export enum AllRegisteredBusinessSteps {
  BUSINESS_INFORMATION = 0,
  BUSINESS_OWNERS_INFORMATION = 1,
  BUSINESS_DOCUMENTS = 2,
  COMPLETE_ONBOARDING = 3,
}

interface IRegisteredBusiness {
  onComplete: () => void;
  onBack?: () => void;
  onBackHandlerReady?: (handler: () => void) => void;
  formData?: any;
  onStepChange?: (step: number) => void;
  navigateToStep?: number; // Allow parent to control which step to navigate to
  resumeCustomerData?: ICorporateCustomer;
}

const RegisteredBusiness: React.FC<IRegisteredBusiness> = ({
  onComplete,
  onBack,
  onBackHandlerReady,
  formData = {},
  onStepChange,
  navigateToStep,
  resumeCustomerData,
}) => {
  const [currentStep, setCurrentStep] = useState(
    AllRegisteredBusinessSteps.BUSINESS_INFORMATION
  );

  const { data: registeredBusinessDocuments, isLoading: isLoadingDocuments } =
    useDocumentsByCustomerSubType(BusinessTypes.REGISTERED_BUSINESS.toString());

  // Sync with parent stepper navigation (subtract 1 because parent stepper has "Get Started" as step 0)
  useEffect(() => {
    if (navigateToStep !== undefined && navigateToStep >= 1) {
      const internalStep = navigateToStep - 1;
      if (
        internalStep !== currentStep &&
        internalStep >= 0 &&
        internalStep <= AllRegisteredBusinessSteps.COMPLETE_ONBOARDING
      ) {
        setCurrentStep(internalStep);
      }
    }
  }, [navigateToStep]);

  const handleProceed = (_step: number) => {
    setCurrentStep(_step);
    // Notify parent about step change (add 1 because "Get Started" is step 0 in parent stepper)
    if (onStepChange) {
      onStepChange(_step + 1);
    }
  };

  // Notify parent when current step changes
  useEffect(() => {
    if (onStepChange) {
      onStepChange(currentStep + 1);
    }
  }, [currentStep, onStepChange]);

  const handleBackStep = () => {
    // Navigate backwards through steps
    switch (currentStep) {
      case AllRegisteredBusinessSteps.COMPLETE_ONBOARDING:
        setCurrentStep(AllRegisteredBusinessSteps.BUSINESS_DOCUMENTS);
        if (onStepChange) {
          onStepChange(AllRegisteredBusinessSteps.BUSINESS_DOCUMENTS + 1);
        }
        break;
      case AllRegisteredBusinessSteps.BUSINESS_DOCUMENTS:
        setCurrentStep(AllRegisteredBusinessSteps.BUSINESS_OWNERS_INFORMATION);
        if (onStepChange) {
          onStepChange(
            AllRegisteredBusinessSteps.BUSINESS_OWNERS_INFORMATION + 1
          );
        }
        break;
      case AllRegisteredBusinessSteps.BUSINESS_OWNERS_INFORMATION:
        setCurrentStep(AllRegisteredBusinessSteps.BUSINESS_INFORMATION);
        if (onStepChange) {
          onStepChange(AllRegisteredBusinessSteps.BUSINESS_INFORMATION + 1);
        }
        break;
      case AllRegisteredBusinessSteps.BUSINESS_INFORMATION:
        // We're at the first step, go back to parent step
        if (onBack) {
          onBack();
        }
        break;
      default:
        break;
    }
  };

  // Expose the back handler to parent via callback
  useEffect(() => {
    if (onBackHandlerReady) {
      onBackHandlerReady(handleBackStep);
    }
  }, [currentStep, onBackHandlerReady]);

  const getStepTitle = () => {
    switch (currentStep) {
      case AllRegisteredBusinessSteps.BUSINESS_INFORMATION:
        return "Business Information";
      case AllRegisteredBusinessSteps.BUSINESS_OWNERS_INFORMATION:
        return "Business Owner's Information";
      case AllRegisteredBusinessSteps.BUSINESS_DOCUMENTS:
        return "Business Documents";
      case AllRegisteredBusinessSteps.COMPLETE_ONBOARDING:
        return "Complete Onboarding";
      default:
        return "";
    }
  };

  const getStepSubtitle = () => {
    if (currentStep === AllRegisteredBusinessSteps.COMPLETE_ONBOARDING) {
      return "Review your onboarding progress and complete any outstanding steps.";
    }
    return "Kindly enter your information below";
  };

  return (
    <div className={styles.registeredBusiness}>
      <HeaderTitle>{getStepTitle()}</HeaderTitle>
      <p className={styles.subtitle}>{getStepSubtitle()}</p>

      <div className={styles.content}>
        {(() => {
          switch (currentStep) {
            case AllRegisteredBusinessSteps.BUSINESS_INFORMATION:
              return (
                <BusinessInformation
                  resumeCustomerData={resumeCustomerData!}
                  handleProceed={handleProceed}
                  formData={formData}
                />
              );

            case AllRegisteredBusinessSteps.BUSINESS_OWNERS_INFORMATION:
              return (
                <BusinessOwnersInformation
                  resumeCustomerData={resumeCustomerData!}
                  handleProceed={handleProceed}
                  formData={formData}
                />
              );

            case AllRegisteredBusinessSteps.BUSINESS_DOCUMENTS:
              return (
                <RegisteredBusinessDocuments
                  resumeCustomerData={resumeCustomerData!}
                  handleProceed={handleProceed}
                  formData={formData}
                  documentTypes={registeredBusinessDocuments?.data!}
                />
              );

            case AllRegisteredBusinessSteps.COMPLETE_ONBOARDING:
              return (
                <CompleteOnboarding
                  resumeCustomerData={resumeCustomerData!}
                  setCurrentStep={setCurrentStep}
                  onComplete={onComplete}
                  formData={formData}
                />
              );

            default:
              return null;
          }
        })()}
      </div>
    </div>
  );
};

export default RegisteredBusiness;
